import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Experience } from '../../types';
import { formatMonth, toDateInput, toStringOrNull } from '../../utils/format';
import ConfirmDialog from '../common/ConfirmDialog';

interface Props {
  items: Experience[];
  onCreate: (data: Partial<Experience>) => Promise<void>;
  onUpdate: (id: Experience['id'], data: Partial<Experience>) => Promise<void>;
  onDelete: (id: Experience['id']) => Promise<void>;
}

interface FormState {
  employer: string;
  title: string;
  location: string;
  start_date: string;
  end_date: string;
  is_current: boolean;
  description: string;
}

const EMPTY: FormState = {
  employer: '',
  title: '',
  location: '',
  start_date: '',
  end_date: '',
  is_current: false,
  description: '',
};

function toForm(item: Experience): FormState {
  return {
    employer: item.employer ?? '',
    title: item.title ?? '',
    location: item.location ?? '',
    start_date: toDateInput(item.start_date),
    end_date: toDateInput(item.end_date),
    is_current: Boolean(item.is_current),
    description: item.description ?? '',
  };
}

function toPayload(form: FormState): Partial<Experience> {
  return {
    employer: form.employer.trim(),
    title: form.title.trim(),
    location: form.location.trim(),
    start_date: toStringOrNull(form.start_date),
    end_date: form.is_current ? null : toStringOrNull(form.end_date),
    is_current: form.is_current,
    description: form.description.trim(),
  };
}

const ExperienceSection: React.FC<Props> = ({ items, onCreate, onUpdate, onDelete }) => {
  const [editing, setEditing] = useState<{ id: Experience['id'] | null; form: FormState } | null>(null);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Experience | null>(null);
  const [deleting, setDeleting] = useState(false);

  const set = (key: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setEditing((s) => (s ? { ...s, form: { ...s.form, [key]: e.target.value } } : s));

  const canSave = Boolean(editing?.form.employer.trim() && editing?.form.title.trim()) && !saving;

  const handleSave = async () => {
    if (!editing || !canSave) return;
    setSaving(true);
    try {
      const payload = toPayload(editing.form);
      if (editing.id === null) {
        await onCreate({ ...payload, ordering: items.length });
      } else {
        await onUpdate(editing.id, payload);
      }
      setEditing(null);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await onDelete(pendingDelete.id);
      setPendingDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>
          Experience
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setEditing({ id: null, form: EMPTY })}
        >
          Add experience
        </Button>
      </Stack>

      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No work experience added yet.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {items.map((item) => (
            <Card key={item.id} variant="outlined">
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="subtitle1" fontWeight={600}>
                        {item.title}
                      </Typography>
                      {item.is_current && <Chip label="Current" size="small" color="success" variant="outlined" />}
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {[item.employer, item.location].filter(Boolean).join(' | ')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[formatMonth(item.start_date), item.is_current ? 'Present' : formatMonth(item.end_date)]
                        .filter(Boolean)
                        .join(' - ')}
                    </Typography>
                    {item.description && (
                      <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                        {item.description}
                      </Typography>
                    )}
                  </Box>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => setEditing({ id: item.id, form: toForm(item) })}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => setPendingDelete(item)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      <Dialog open={Boolean(editing)} onClose={() => !saving && setEditing(null)} fullWidth maxWidth="sm">
        <DialogTitle>{editing?.id === null ? 'Add experience' : 'Edit experience'}</DialogTitle>
        <DialogContent>
          {editing && (
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={12} sm={6}>
                <TextField label="Employer" value={editing.form.employer} onChange={set('employer')} fullWidth required autoFocus />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Title" value={editing.form.title} onChange={set('title')} fullWidth required />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Location" value={editing.form.location} onChange={set('location')} fullWidth />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Start date" type="date" value={editing.form.start_date} onChange={set('start_date')} fullWidth InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="End date"
                  type="date"
                  value={editing.form.is_current ? '' : editing.form.end_date}
                  onChange={set('end_date')}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  disabled={editing.form.is_current}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editing.form.is_current}
                      onChange={(e) =>
                        setEditing((s) =>
                          s ? { ...s, form: { ...s.form, is_current: e.target.checked } } : s
                        )
                      }
                    />
                  }
                  label="I currently work here"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Description" value={editing.form.description} onChange={set('description')} fullWidth multiline minRows={3} />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={!canSave}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete this experience entry?"
        message={
          pendingDelete ? `${pendingDelete.title} at ${pendingDelete.employer} will be removed.` : undefined
        }
        confirmLabel="Delete"
        confirmColor="error"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </Box>
  );
};

export default ExperienceSection;
